import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificacoesApi, categoriaDoTipo, type Notificacao, type NotificacoesResposta } from '../api/notificacoes';
import { useSignalR } from './useSignalR';
import { useNotificacaoPrefs } from './useNotificacaoPrefs';
import { useToast } from '../components/ui/Toast';

const KEY = ['notificacoes'] as const;

/**
 * Lista de notificações + contagem de não-lidas, com push em tempo real.
 * O GET é a fonte da verdade; o push do SignalR só faz patch otimista da cache
 * (mais snappy que invalidar). Se o push cair, o refetch reconcilia.
 */
export function useNotificacoes(top = 30) {
  const qc = useQueryClient();
  const { connection } = useSignalR();
  const toast = useToast();
  const { silenciado } = useNotificacaoPrefs();

  const query = useQuery({
    queryKey: KEY,
    queryFn: () => notificacoesApi.listar(false, top),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!connection) return;
    const handler = (dto: Notificacao) => {
      // O push chega pro GRUPO inteiro (perm:x) — o filtro por preferência é
      // por-usuário, então tem que ser aqui. O GET já vem filtrado pelo backend.
      if (silenciado(categoriaDoTipo(dto.tipo))) return;

      // Dedup ANTES de tocar cache/toast: um ADMIN com a claim do escopo cai nos
      // grupos perm:{x} E admin, então o mesmo push pode chegar 2×. O primeiro
      // adiciona; os repetidos são no-op (sem re-toast).
      const atual = qc.getQueryData<NotificacoesResposta>(KEY);
      if (atual?.itens.some((n) => n.id === dto.id)) return;

      qc.setQueryData<NotificacoesResposta>(KEY, (prev) => {
        const base: NotificacoesResposta = prev ?? { itens: [], naoLidas: 0 };
        if (base.itens.some((n) => n.id === dto.id)) return base; // dedup push↔refetch
        return {
          itens: [{ ...dto, lida: false }, ...base.itens].slice(0, top),
          naoLidas: base.naoLidas + 1,
        };
      });
      // Feedback imediato — mapeia severidade do domínio → tom do toast.
      const disparar =
        dto.severidade === 'critico' ? toast.erro : dto.severidade === 'alerta' ? toast.aviso : toast.info;
      disparar(dto.titulo);
    };
    connection.on('ReceberNotificacao', handler);
    return () => {
      connection.off('ReceberNotificacao', handler);
    };
  }, [connection, qc, toast, top, silenciado]);

  return query;
}

export function useMarcarNotificacaoLida() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificacoesApi.marcarLida(id),
    onSuccess: (_d, id) => {
      qc.setQueryData<NotificacoesResposta>(KEY, (prev) => {
        if (!prev) return prev;
        const jaLida = prev.itens.find((n) => n.id === id)?.lida;
        return {
          itens: prev.itens.map((n) => (n.id === id ? { ...n, lida: true } : n)),
          naoLidas: jaLida ? prev.naoLidas : Math.max(0, prev.naoLidas - 1),
        };
      });
    },
  });
}

export function useMarcarTodasLidas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificacoesApi.marcarTodasLidas(),
    onSuccess: () => {
      qc.setQueryData<NotificacoesResposta>(KEY, (prev) =>
        prev ? { itens: prev.itens.map((n) => ({ ...n, lida: true })), naoLidas: 0 } : prev,
      );
    },
  });
}

export function useDispensarNotificacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => notificacoesApi.dispensar(id),
    onSuccess: (_d, id) => {
      qc.setQueryData<NotificacoesResposta>(KEY, (prev) => {
        if (!prev) return prev;
        const item = prev.itens.find((n) => n.id === id);
        const eraNaoLida = item ? !item.lida : false;
        return {
          itens: prev.itens.filter((n) => n.id !== id),
          naoLidas: eraNaoLida ? Math.max(0, prev.naoLidas - 1) : prev.naoLidas,
        };
      });
    },
  });
}

export function useDispensarTodas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificacoesApi.dispensarTodas(),
    // Backend dispensa TODAS as visíveis (não só o top exibido) → feed zera.
    onSuccess: () => qc.setQueryData<NotificacoesResposta>(KEY, { itens: [], naoLidas: 0 }),
  });
}
